<template>
  <div class="viewport-wrapper" @contextmenu.prevent>
    <div
      class="container"
      @mousemove="handleMouseMove"
      @mousedown="handleMouseClick"
      @click.prevent
    >
      <div class="time-display">
        <div class="solar-time">{{ currentTime }}</div>
        <div class="lunar-time">{{ lunarDate }}</div>
      </div>
      <canvas ref="canvas" class="minion-canvas"></canvas>
    </div>
  </div>
</template>

<script>
import { solarToLunar } from "lunar-calendar";
export default {
  name: "LunarDate",
  data() {
    return {
      mousePos: { x: 0, y: 0 },
      canvasSize: 400,
      animationFrame: null,
      sizeRatio: 0.62,
      leftClickEffects: [],
      rightClickEffects: [],
      currentTime: "",
      lunarDate: "",
    };
  },
  mounted() {
    this.initCanvas();
    this.draw();
    window.addEventListener("resize", this.handleResize);
    this.handleResize();

    // 新增时间更新逻辑
    this.updateTime();
    this.timeInterval = setInterval(this.updateTime, 1000);
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.handleResize);
    cancelAnimationFrame(this.animationFrame);
    // 新增清除定时器
    clearInterval(this.timeInterval);
  },
  methods: {
    updateTime() {
      const now = new Date();

      // 公历时间保持不变
      this.currentTime = now
        .toLocaleString("zh-CN", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        })
        .replace(/\//g, "-");

      // 获取农历信息
      const lunar = solarToLunar(
        now.getFullYear(),
        now.getMonth() + 1,
        now.getDate()
      );

      if (lunar) {
        let specialInfo = [];

        // 添加节气信息
        if (lunar.solarTerm) {
          specialInfo.push(`节气：${lunar.solarTerm}`);
        }

        // 添加节日信息（优先显示农历节日）
        if (lunar.lunarFestival) {
          specialInfo.push(`节日：${lunar.lunarFestival}`);
        } else if (lunar.gregorianFestival) {
          specialInfo.push(`节日：${lunar.gregorianFestival}`);
        }

        // 组合最终字符串
        const baseDate = `农历 ${lunar.lunarYear}年 ${lunar.lunarMonthName}${lunar.lunarDayName}`;
        this.lunarDate = [baseDate, ...specialInfo].join(" | ");
      } else {
        this.lunarDate = "农历日期加载中...";
      }
    },
    initCanvas() {
      const canvas = this.$refs.canvas;
      const dpr = window.devicePixelRatio || 1;

      canvas.width = this.canvasSize * dpr;
      canvas.height = this.canvasSize * dpr;
      canvas.style.width = `${this.canvasSize}px`;
      canvas.style.height = `${this.canvasSize}px`;
    },

    draw() {
      const canvas = this.$refs.canvas;
      const ctx = canvas.getContext("2d");
      const center = canvas.width / 2;
      const headRadius = center * this.sizeRatio;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // 绘制头部
      ctx.beginPath();
      ctx.arc(center, center, headRadius, 0, Math.PI * 2);
      ctx.fillStyle = "#FFDF00";
      ctx.fill();
      ctx.strokeStyle = "#000";
      ctx.lineWidth = 4;
      ctx.stroke();

      // 绘制眼睛
      const eyeOffsetX = headRadius * 0.38;
      const eyeOffsetY = headRadius * 0.18;
      const eyeRadius = headRadius * 0.12;
      const eyePositions = [
        { x: center - eyeOffsetX, y: center - eyeOffsetY },
        { x: center + eyeOffsetX, y: center - eyeOffsetY },
      ];

      eyePositions.forEach((pos) => {
        // 眼白
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, eyeRadius, 0, Math.PI * 2);
        ctx.fillStyle = "white";
        ctx.fill();
        ctx.stroke();

        // 瞳孔
        const dx = this.mousePos.x - pos.x;
        const dy = this.mousePos.y - pos.y;
        const angle = Math.atan2(dy, dx);
        const maxDistance = eyeRadius * 0.8;
        const distance = Math.min(Math.hypot(dx, dy), maxDistance);

        ctx.beginPath();
        ctx.arc(
          pos.x + Math.cos(angle) * distance,
          pos.y + Math.sin(angle) * distance,
          eyeRadius * 0.4,
          0,
          Math.PI * 2
        );
        ctx.fillStyle = "#2C3E50";
        ctx.fill();
      });

      // 绘制嘴巴
      this.drawMouth(ctx, center, headRadius);

      // 绘制特效
      this.drawEffects(this.leftClickEffects, "#4CAF50");
      this.drawEffects(this.rightClickEffects, "#F44336");

      this.animationFrame = requestAnimationFrame(this.draw);
    },

    drawMouth(ctx, center, headRadius) {
      const mouthWidth = headRadius * 0.6;
      const startY = center + headRadius * 0.3;
      const ctrlY = center + headRadius * 0.45;

      ctx.beginPath();
      ctx.moveTo(center - mouthWidth, startY);
      ctx.quadraticCurveTo(center, ctrlY, center + mouthWidth, startY);
      ctx.lineWidth = 4;
      ctx.strokeStyle = "#2C3E50";
      ctx.stroke();
    },

    handleMouseMove(event) {
      const rect = this.$refs.canvas.getBoundingClientRect();
      const scale = this.$refs.canvas.width / this.$refs.canvas.offsetWidth;

      this.mousePos = {
        x: (event.clientX - rect.left) * scale,
        y: (event.clientY - rect.top) * scale,
      };
    },

    handleMouseClick(event) {
      const rect = this.$refs.canvas.getBoundingClientRect();
      const scale = this.$refs.canvas.width / this.$refs.canvas.offsetWidth;
      const pos = {
        x: (event.clientX - rect.left) * scale,
        y: (event.clientY - rect.top) * scale,
      };

      if (event.button === 0) {
        // 左键
        this.createParticles(pos, {
          color: "#4CAF50",
          count: 12,
          size: 3,
          speed: 8,
        });
      } else if (event.button === 2) {
        // 右键
        this.createParticles(pos, {
          color: "#F44336",
          count: 8,
          size: 5,
          speed: 12,
        });
      }
    },

    createParticles(pos, config) {
      const particles = [];
      for (let i = 0; i < config.count; i++) {
        particles.push({
          x: pos.x,
          y: pos.y,
          color: this.colorVariant(config.color),
          radius: Math.random() * config.size + 2,
          alpha: 1,
          velocity: {
            x: (Math.random() - 0.5) * config.speed,
            y: (Math.random() - 0.5) * config.speed,
          },
        });
      }

      if (event.button === 0) {
        this.leftClickEffects.push(...particles);
      } else {
        this.rightClickEffects.push(...particles);
      }
    },

    drawEffects(particles) {
      const ctx = this.$refs.canvas.getContext("2d");
      particles.forEach((p, index) => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = p.color.replace(/[\d.]+\)$/, p.alpha + ")");
        ctx.fill();

        // 更新粒子状态
        p.x += p.velocity.x;
        p.y += p.velocity.y;
        p.alpha *= 0.92;
        p.radius *= 0.96;

        if (p.alpha < 0.1) {
          particles.splice(index, 1);
        }
      });
    },

    colorVariant(baseColor) {
      const hsl = this.hexToHSL(baseColor);
      hsl.l += Math.random() * 20 - 10;
      return `hsla(${hsl.h}, ${hsl.s}%, ${hsl.l}%, 0.8)`;
    },

    hexToHSL(hex) {
      hex = hex.replace(/^#/, "");
      const r = parseInt(hex.substring(0, 2), 16) / 255;
      const g = parseInt(hex.substring(2, 4), 16) / 255;
      const b = parseInt(hex.substring(4, 6), 16) / 255;

      const max = Math.max(r, g, b),
        min = Math.min(r, g, b);
      let h,
        s,
        l = (max + min) / 2;

      if (max === min) {
        h = s = 0;
      } else {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
          case r:
            h = (g - b) / d + (g < b ? 6 : 0);
            break;
          case g:
            h = (b - r) / d + 2;
            break;
          case b:
            h = (r - g) / d + 4;
            break;
        }
        h /= 6;
      }

      return { h: h * 360, s: s * 100, l: l * 100 };
    },

    handleResize() {
      const viewportSize = Math.min(window.innerWidth, window.innerHeight);
      this.canvasSize = viewportSize * 0.45;
      this.initCanvas();
    },
  },
};
</script>

<style scoped>
.viewport-wrapper {
  width: 100vw;
  height: 80vh;
  display: flex;
  justify-content: center;
  align-items: center;
  touch-action: none;
  user-select: none;
  transform: translate(-50%, -50%);
  top: 60%;
  left: 50%;
  position: fixed;
}

.container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3vh;
}

.minion-canvas {
  width: 75vmin;
  height: 75vmin;
  max-width: 400px;
  max-height: 400px;
  border: 3px solid rgba(255, 255, 255, 0.9);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.15);
  border-radius: 15%;
  transition: transform 0.2s ease;
  cursor: pointer;
}
.time-display {
  position: absolute;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  text-align: center;
  z-index: 10;
  background: rgba(255, 255, 255, 0.9);
  padding: 8px 20px;
  border-radius: 25px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.solar-time {
  font-size: 1.2em;
  font-weight: 600;
  color: #2c3e50;
}

.lunar-time {
  font-size: 0.9em;
  color: #7f8c8d;
  margin-top: 4px;
}

@media (max-width: 768px) {
  .minion-canvas {
    width: 65vmin;
    height: 65vmin;
  }
}
</style>
